import { useState } from "react";

import { putRealmSecret } from "../lib/api";
import { describeApiError } from "../lib/apiError";
import { parseStringMap } from "../lib/jsonBundle";

/**
 * One stored secret. The name is read-only; the value is write-only — never
 * fetched or pre-filled. The operator supplies a new bundle, which the server
 * encrypts (per-realm derived Fernet) in place of the old ciphertext.
 */
export default function SecretRow({ realmId, name }: { realmId: string; name: string }) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [stored, setStored] = useState(false);

  function reset() {
    setOpen(false);
    setValue("");
    setErr("");
    setBusy(false);
  }

  async function save() {
    setErr("");
    let bundle: Record<string, string>;
    try {
      bundle = parseStringMap(value);
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : String(e));
      return;
    }
    setBusy(true);
    try {
      await putRealmSecret(realmId, name, bundle);
      reset();
      setStored(true);
      setTimeout(() => setStored(false), 2000);
    } catch (e: unknown) {
      setErr(describeApiError(e).message);
      setBusy(false);
    }
  }

  return (
    <li className="space-y-2">
      <div className="flex items-center gap-3">
        <span className="font-mono text-kw-text-primary">{name}</span>
        {!open && (
          <button onClick={() => setOpen(true)} className="text-kw-l1 hover:underline">
            Set new value ▸
          </button>
        )}
        {stored && <span className="text-kw-l2">✓ stored</span>}
      </div>
      {open && (
        <div className="space-y-2 pl-1">
          <textarea
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={'{"refresh_token": "..."}'}
            rows={3}
            className="w-full font-mono text-xs bg-kw-bg-elevated rounded p-2 text-kw-text-primary border border-kw-border-subtle focus:outline-none focus:border-kw-l1"
          />
          <div className="flex items-center gap-3">
            <button
              onClick={save}
              disabled={busy}
              className="px-2 py-1 rounded bg-kw-l1 text-black disabled:opacity-50"
            >
              {busy ? "Saving…" : "Save"}
            </button>
            <button
              onClick={reset}
              disabled={busy}
              className="text-kw-text-muted hover:text-kw-text-primary disabled:opacity-50"
            >
              Cancel
            </button>
            {err && <span className="text-red-400">{err}</span>}
          </div>
          <div className="text-kw-text-faint">
            The value is encrypted server-side and replaces the existing one. The current value
            is never shown.
          </div>
        </div>
      )}
    </li>
  );
}
